import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { checkActionExecution } from "@redotech/redo-web/actions/action-definition";
import {
  conversationActionIds,
  type ConversationActionId,
} from "@redotech/redo-web/actions/conversation-actions/action-ids";
import {
  priorityScope,
  snoozeScope,
} from "@redotech/redo-web/actions/conversation-actions/scope-trees";
import { RedoCommandShortcut } from "@redotech/redo-web/arbiter-components/command-menu/redo-command-shortcut";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@redotech/redo-web/components/ui/dropdown-menu";
import { getShortcutDisplayParts } from "@redotech/redo-web/keyboard-shortcuts/keyboard-shortcut";
import { useKeyboardShortcuts } from "@redotech/redo-web/keyboard-shortcuts/use-keyboard-shortcuts";
import { memo, useCallback, useMemo, useState } from "react";
import { SnoozeCustomModal } from "../components/snooze-custom-modal";
import { TagSelectorDropdownContent } from "../components/tag-selector-dropdown-content";
import { getAction } from "./action-registry";
import { DeleteConversationConfirmModal } from "./delete-conversation-confirm-modal";
import { ScopeMenu } from "./scope-menu";
import {
  useAddTagConversationAction,
  useConversationActionHandlers,
  useSnoozeConversationAction,
  type ConversationActionHandlerMap,
} from "./use-conversation-actions";

const STATUS_ACTION_IDS = [
  conversationActionIds.close,
  conversationActionIds.reopen,
  conversationActionIds.markInProgress,
  conversationActionIds.archive,
] as const;

const READ_ACTION_IDS = [
  conversationActionIds.markRead,
  conversationActionIds.markUnread,
] as const;

const DANGER_ACTION_IDS = [conversationActionIds.delete] as const;

export interface ConversationActionsDropdownProps {
  conversationId: ConversationId;
  conversation: ConversationListItemResponse;
  isOpen: boolean;
  onClose: () => void;
  onMergeTicket?: () => void;
  align?: "start" | "center" | "end";
}

export const ConversationActionsDropdown = memo(
  function ConversationActionsDropdown({
    conversationId,
    conversation,
    isOpen,
    onClose,
    onMergeTicket,
    align = "end",
  }: ConversationActionsDropdownProps) {
    const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null);
    const [customSnoozeOpen, setCustomSnoozeOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const snoozeAction = useSnoozeConversationAction();
    const handlers = useConversationActionHandlers(
      conversationId,
      onMergeTicket,
    );

    const handlersWithCustomSnooze = useMemo(
      () => ({
        ...handlers,
        [conversationActionIds.snoozeCustom]: () => {
          setCustomSnoozeOpen(true);
          onClose();
        },
      }),
      [handlers, onClose],
    );

    const handlersWithDeleteConfirm = useMemo(
      () => ({
        ...handlers,
        [conversationActionIds.delete]: () => {
          setDeleteConfirmOpen(true);
        },
      }),
      [handlers],
    );

    const handleDeleteConfirm = useCallback(() => {
      handlers[conversationActionIds.delete]?.();
      setDeleteConfirmOpen(false);
    }, [handlers]);

    const handleDeleteCancel = useCallback(() => {
      setDeleteConfirmOpen(false);
    }, []);

    const addTagConversationAction = useAddTagConversationAction();
    const handleTagsChanged = useCallback(
      (
        newTagIds: Parameters<
          typeof addTagConversationAction.execute
        >[1]["tagIds"],
      ) => {
        void addTagConversationAction.execute(conversationId, {
          tagIds: newTagIds,
        });
      },
      [addTagConversationAction, conversationId],
    );

    const bindings = useMemo(() => {
      if (!conversationId || !conversation) {
        return [];
      }

      const simpleActionIds = [
        ...STATUS_ACTION_IDS,
        conversationActionIds.mergeTicket,
        ...READ_ACTION_IDS,
        ...DANGER_ACTION_IDS,
      ];

      const actionBindings = simpleActionIds
        .map((id) => {
          const action = getAction(id);
          if (!action.shortcut) {
            return null;
          }
          return {
            shortcut: action.shortcut,
            handler: () => {
              const check = checkActionExecution(action, conversation);
              if (!check.canExecute) {
                return;
              }
              handlersWithDeleteConfirm[id]?.();
              onClose();
            },
          };
        })
        .filter(
          (binding): binding is NonNullable<typeof binding> => binding !== null,
        );

      const setPriorityAction = getAction(conversationActionIds.setPriority);
      const setSnoozeAction = getAction(conversationActionIds.snooze);
      const submenuTriggerBindings = [
        setPriorityAction.shortcut
          ? {
              shortcut: setPriorityAction.shortcut,
              handler: () => setOpenSubmenuId("priority"),
            }
          : null,
        setSnoozeAction.shortcut
          ? {
              shortcut: setSnoozeAction.shortcut,
              handler: () => setOpenSubmenuId("snooze"),
            }
          : null,
      ].filter(
        (binding): binding is NonNullable<typeof binding> => binding !== null,
      );

      return [...actionBindings, ...submenuTriggerBindings];
    }, [conversation, conversationId, onClose, handlersWithDeleteConfirm]);

    useKeyboardShortcuts({
      bindings,
      scope: "actionMenu",
      enabled: isOpen && openSubmenuId === null,
    });

    const renderActionItem = useCallback(
      (
        actionId: ConversationActionId,
        actionHandlers: ConversationActionHandlerMap,
        conv: ConversationListItemResponse,
        handleClose: () => void,
        className?: string,
      ) => {
        const action = getAction(actionId);
        const check = checkActionExecution(action, conv);
        if (action.isVisible && !action.isVisible(conv)) {
          return null;
        }
        return (
          <DropdownMenuItem
            className={className}
            disabled={!check.canExecute}
            key={actionId}
            onClick={() => {
              actionHandlers[actionId]?.();
              handleClose();
            }}
          >
            <div className="flex w-full items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                {action.Icon && <action.Icon className="h-4 w-4" />}
                <span>{action.title}</span>
              </div>
              {action.shortcut && (
                <RedoCommandShortcut
                  hotkey={getShortcutDisplayParts(action.shortcut)}
                />
              )}
            </div>
          </DropdownMenuItem>
        );
      },
      [],
    );

    const setPriorityAction = getAction(conversationActionIds.setPriority);
    const isPriorityVisible =
      setPriorityAction.isVisible?.(conversation) !== false;
    const isPriorityEnabled = checkActionExecution(
      setPriorityAction,
      conversation,
    ).canExecute;

    const setSnoozeAction = getAction(conversationActionIds.snooze);
    const isSnoozeVisible = setSnoozeAction.isVisible?.(conversation) !== false;
    const isSnoozeEnabled = checkActionExecution(
      setSnoozeAction,
      conversation,
    ).canExecute;

    const addTagAction = getAction(conversationActionIds.addTag);
    const isAddTagVisible = addTagAction.isVisible?.(conversation) !== false;

    return (
      <>
        <DropdownMenuContent
          align={align}
          className="[&_[data-slot=dropdown-menu-item]]:text-xs [&_[data-slot=dropdown-menu-sub-trigger]]:text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          {STATUS_ACTION_IDS.map((id) =>
            renderActionItem(id, handlers, conversation, onClose),
          )}

          {renderActionItem(
            conversationActionIds.mergeTicket,
            handlers,
            conversation,
            onClose,
          )}

          <DropdownMenuSeparator />

          {READ_ACTION_IDS.map((id) =>
            renderActionItem(id, handlers, conversation, onClose),
          )}

          <DropdownMenuSeparator />

          {isPriorityVisible && (
            <DropdownMenuSub
              onOpenChange={(open) =>
                setOpenSubmenuId(open ? "priority" : null)
              }
              open={openSubmenuId === "priority"}
            >
              <DropdownMenuSubTrigger disabled={!isPriorityEnabled}>
                <div className="flex w-full items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    {setPriorityAction.Icon && (
                      <setPriorityAction.Icon className="h-4 w-4" />
                    )}
                    <span>{setPriorityAction.title}</span>
                  </div>
                  {setPriorityAction.shortcut && (
                    <RedoCommandShortcut
                      hotkey={getShortcutDisplayParts(
                        setPriorityAction.shortcut,
                      )}
                    />
                  )}
                </div>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <ScopeMenu
                  conversation={conversation}
                  depth={1}
                  handlers={handlers}
                  onClose={onClose}
                  scope={priorityScope}
                />
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          {isSnoozeVisible && (
            <DropdownMenuSub
              onOpenChange={(open) => setOpenSubmenuId(open ? "snooze" : null)}
              open={openSubmenuId === "snooze"}
            >
              <DropdownMenuSubTrigger disabled={!isSnoozeEnabled}>
                <div className="flex w-full items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    {setSnoozeAction.Icon && (
                      <setSnoozeAction.Icon className="h-4 w-4" />
                    )}
                    <span>{setSnoozeAction.title}</span>
                  </div>
                  {setSnoozeAction.shortcut && (
                    <RedoCommandShortcut
                      hotkey={getShortcutDisplayParts(setSnoozeAction.shortcut)}
                    />
                  )}
                </div>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <ScopeMenu
                  conversation={conversation}
                  depth={1}
                  handlers={handlersWithCustomSnooze}
                  onClose={onClose}
                  scope={snoozeScope}
                />
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          {isAddTagVisible && (
            <DropdownMenuSub
              onOpenChange={(open) => setOpenSubmenuId(open ? "addTag" : null)}
              open={openSubmenuId === "addTag"}
            >
              <DropdownMenuSubTrigger>
                <div className="flex w-full items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    {addTagAction.Icon && (
                      <addTagAction.Icon className="h-4 w-4" />
                    )}
                    <span>{addTagAction.title}</span>
                  </div>
                  {addTagAction.shortcut && (
                    <RedoCommandShortcut
                      hotkey={getShortcutDisplayParts(addTagAction.shortcut)}
                    />
                  )}
                </div>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <TagSelectorDropdownContent
                  currentTagIds={conversation.tagIds}
                  onTagsChanged={handleTagsChanged}
                />
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          <DropdownMenuSeparator />

          {DANGER_ACTION_IDS.map((id) =>
            renderActionItem(
              id,
              handlersWithDeleteConfirm,
              conversation,
              onClose,
              "text-red-600 focus:text-red-600",
            ),
          )}
        </DropdownMenuContent>
        <DeleteConversationConfirmModal
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          open={deleteConfirmOpen}
        />
        {customSnoozeOpen && (
          <SnoozeCustomModal
            conversationId={conversationId}
            onClose={() => setCustomSnoozeOpen(false)}
            onConfirm={(id, until) => {
              void snoozeAction.execute(id, { duration: "custom", until });
            }}
          />
        )}
      </>
    );
  },
);
